// src/pages/Users.tsx

import React, { useState } from 'react';
import { Shield, UserPlus, Edit2, Trash2, Mail, User, Lock, CheckCircle } from 'lucide-react';
import { usersService } from 'api/services/users.service';
import { User as UserType, PaginatedResponse } from 'types';
import { useApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';
import Pagination from 'components/Common/Pagination';
import UserModal from 'components/Modals/UserModal';
import OrganizationFilter from 'components/Filters/OrganizationFilter';
import { getRoleColor } from 'utils/helpers';
import { ITEMS_PER_PAGE } from 'utils/constants';

const Users: React.FC = () => {
  const { user: currentUser, currentOrganization } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'master_admin' | 'super_admin' | 'service_admin' | 'service_user' | 'operator_admin' | 'operator_user'>('all');
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'service' | 'business'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedOrgId, setSelectedOrgId] = useState<string | 'all'>('all');

  const currentRole = currentOrganization?.role || currentUser?.role;
  const currentRoleLower = currentRole?.toLowerCase();

  // Determine organizationId to pass to API
  const getOrganizationId = () => {
    if (currentRoleLower === 'master_admin' || currentRoleLower === 'super_admin' || currentRoleLower === 'service_admin') {
      return selectedOrgId === 'all' ? undefined : selectedOrgId;
    }
    return currentOrganization?.id;
  };

  const { data, loading, error, refetch } = useApi<PaginatedResponse<UserType>>(
    () => usersService.getUsers({
      page: currentPage,
      perPage: ITEMS_PER_PAGE,
      search: searchTerm,
      role: roleFilter !== 'all' ? roleFilter : undefined,
      organizationId: getOrganizationId()
    }),
    [currentPage, searchTerm, roleFilter, selectedOrgId, currentOrganization?.id]
  );

  const handleAdd = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEdit = (user: UserType) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (id === currentUser?.id) {
      alert("You cannot delete your own account!");
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    setDeleting(id);
    try {
      const response = await usersService.deleteUser(id);
      if (response.success) {
        refetch();
        // If we deleted the last item on the current page, go to previous page
        if (data && data.data.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } else {
        alert(response.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete user');
    } finally {
      setDeleting(null);
    }
  };

  const handleSave = async (userData: Omit<UserType, 'id'>) => {
    try {
      let response;
      if (editingUser) {
        response = await usersService.updateUser(editingUser.id, userData);
      } else {
        response = await usersService.createUser(userData);
      }
      
      if (response.success) {
        setShowModal(false);
        refetch();
      } else {
        alert(response.error || 'Failed to save user');
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to save user');
    }
  };

  const getRoleBadge = (role: string | undefined) => {
    if (!role) return null;
    const roleIcons = {
      super_admin: <Shield className="w-3 h-3" />,
      admin: <Lock className="w-3 h-3" />,
      operator: <User className="w-3 h-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(role)}`}>
        {roleIcons[role as keyof typeof roleIcons]}
        {role.replace('_', ' ')}
      </span>
    );
  };

  const getStatusBadge = (userId: number) => {
    if (userId === currentUser?.id) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Current User
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
        Active
      </span>
    );
  };

  // Check if user has permission to view this page
  const canManageUsers = currentRoleLower === 'master_admin' ||
                         currentRoleLower === 'super_admin' ||
                         currentRoleLower === 'service_admin' ||
                         currentRoleLower === 'operator_admin';

  if (!canManageUsers) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Shield className="w-16 h-16 text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
        <p className="text-gray-600 text-center max-w-md">
          You do not have permission to access User Management. This feature is only available to Administrators.
        </p>
      </div>
    );
  }

  // Filter users based on logged-in user's role
  const getFilteredUsersByRole = () => {
    if (!data) return [];

    console.log('👥 Users Page Debug:', {
      totalFromAPI: data.total,
      usersArrayLength: data.data?.length,
      currentRole: currentRoleLower,
      userTypeFilter,
      sampleUser: data.data?.[0]
    });

    let filtered = data.data;

    // Master Admin: Can see all users
    if (currentRoleLower === 'master_admin') {
      console.log('👑 Master Admin - Showing all users');
      // No additional filtering needed
    }
    // Super Admin: Can see all users
    else if (currentRoleLower === 'super_admin') {
      console.log('👑 Super Admin - Showing all users');
      // No additional filtering needed
    }
    // Service Admin: Can only see/manage service users (not super admin)
    else if (currentRoleLower === 'service_admin') {
      console.log('🔧 Service Admin - Filtering for service users only');
      filtered = filtered.filter(u =>
        u.user_type?.toLowerCase() === 'service' && u.role?.toLowerCase() !== 'super_admin' && u.role?.toLowerCase() !== 'master_admin'
      );
    }
    // Operator Admin: Can only see/manage users from their own business
    else if (currentRoleLower === 'operator_admin') {
      // Handle both snake_case and camelCase from backend
      const businessId = currentOrganization?.business_id ||
                        (currentOrganization as any)?.businessId ||
                        currentUser?.business_id;
      console.log('🏢 Operator Admin - Filtering for business:', businessId, {
        org_snake_case: currentOrganization?.business_id,
        org_camelCase: (currentOrganization as any)?.businessId,
        user_business_id: currentUser?.business_id
      });
      filtered = filtered.filter(u =>
        u.user_type?.toLowerCase() === 'business' && u.business_id === businessId
      );
    }

    // Apply user type filter
    if (userTypeFilter !== 'all') {
      console.log('🔍 Applying user type filter:', userTypeFilter);
      filtered = filtered.filter(u => u.user_type?.toLowerCase() === userTypeFilter.toLowerCase());
    }

    // Apply status filter (active/inactive)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(u => (statusFilter === 'active' ? u.active : !u.active));
    }

    console.log('✅ Filtered users count:', filtered.length);
    if (filtered.length === 0 && data.data?.length > 0) {
      console.warn('⚠️ All users were filtered out! Check user_type and role fields');
    }

    return filtered;
  };

  if (loading && !data) return <LoadingSpinner />;
  if (error && !data) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data) return null;

  const filteredUsers = getFilteredUsersByRole();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
            User Management
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Total Users: {data.total}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="tl-btn tl-btn-primary"
        >
          <UserPlus className="w-5 h-5" />
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name, email, or username..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {(currentRoleLower === 'master_admin' || currentRoleLower === 'super_admin' || currentRoleLower === 'service_admin') && (
            <OrganizationFilter
              value={selectedOrgId === 'all' ? '' : selectedOrgId}
              onChange={(value) => {
                setSelectedOrgId(value || 'all');
                setCurrentPage(1);
              }}
              className="px-4 py-2"
              allowServiceProviderSelection={true}
            />
          )}
          {(currentRoleLower === 'master_admin' || currentRoleLower === 'super_admin') && (
            <select
              value={userTypeFilter}
              onChange={(e) => {
                setUserTypeFilter(e.target.value as any);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All User Types</option>
              <option value="service">Service Users</option>
              <option value="business">Business Users</option>
            </select>
          )}
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as any);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Roles</option>
            {currentRoleLower === 'master_admin' && (
              <option value="master_admin">Master Admin</option>
            )}
            {(currentRoleLower === 'master_admin' || currentRoleLower === 'super_admin') && (
              <option value="super_admin">Super Admin</option>
            )}
            {(currentRoleLower === 'master_admin' || currentRoleLower === 'super_admin' || currentRoleLower === 'service_admin') && (
              <>
                <option value="service_admin">Service Admin</option>
                <option value="service_user">Service User</option>
              </>
            )}
            <option value="operator_admin">Operator Admin</option>
            <option value="operator_user">Operator User</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as any);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                    {/* User Info */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-sm">
                          {user.firstName?.charAt(0).toUpperCase() || user.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user.name || 'User'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">@{user.username}</p>
                        </div>
                      </div>
                    </td>

                    {/* Contact Info */}
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <a href={`mailto:${user.email}`} className="hover:text-blue-600">
                            {user.email}
                          </a>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {user.id}
                          {user.business_id && ` | Business: ${user.business_id}`}
                        </div>
                      </div>
                    </td>

                    {/* User Type */}
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.user_type?.toLowerCase() === 'service'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-cyan-100 text-cyan-800'
                      }`}>
                        {user.user_type?.toLowerCase() === 'service' ? 'Service' : 'Business'}
                      </span>
                    </td>

                    {/* Role */}
                    <td className="px-6 py-4">
                      {getRoleBadge(user.role)}
                    </td>
                    
                    {/* Status */}
                    <td className="px-6 py-4">
                      {getStatusBadge(user.id)}
                    </td>
                    
                    {/* Actions */}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Edit User"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {user.id !== currentUser?.id && (
                          <button
                            onClick={() => handleDelete(user.id)}
                            disabled={deleting === user.id}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete User"
                          >
                            {deleting === user.id ? (
                              <div className="w-4 h-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
            <Pagination
              currentPage={currentPage}
              totalPages={data.totalPages}
              onPageChange={setCurrentPage}
              totalItems={data.total}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </div>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <UserModal
          user={editingUser}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default Users;